/**
 * Compatibilità dei ruoli professionali della stessa persona nello stesso cantiere (FR-015a…FR-015d,
 * research R-14). Specchio puro di `RoleCompatibility.Evaluate` del modulo cantieri: regole e
 * ruoli arrivano da `GET /api/reference/professional-roles`; il server resta l'arbitro, qui la
 * valutazione serve a disabilitare e spiegare le combinazioni prima dell'invio.
 */

/** Un ruolo del catalogo; `maxHolders` nullo = senza limite di titolari. */
export interface ProfessionalRole {
  readonly code: string;
  readonly label: string;
  readonly shortLabel: string | null;
  readonly maxHolders: number | null;
}

export type RoleRuleKind = 'block' | 'warn';
export type RoleRuleScope = 'any' | 'public' | 'private';

/** Una regola per coppia di ruoli. */
export interface RoleRule {
  readonly code: string;
  readonly ruleGroup: string;
  readonly roleA: string;
  readonly roleB: string;
  readonly kind: RoleRuleKind;
  readonly appliesTo: RoleRuleScope;
  readonly reference: string | null;
  readonly message: string;
}

/** Una regola violata o da confermare, con i due ruoli coinvolti. */
export interface RoleIssue {
  readonly rule: string;
  readonly roles: readonly [string, string];
  readonly message: string;
  readonly reference: string | null;
}

/** Un ruolo a titolare unico già occupato da altri (FR-015d). */
export interface HolderIssue {
  readonly role: string;
  readonly maxHolders: number;
  readonly currentHolders: number;
}

export interface RoleEvaluation {
  readonly blocks: readonly RoleIssue[];
  readonly warnings: readonly RoleIssue[];
  readonly holders: readonly HolderIssue[];
  readonly unknownRoles: readonly string[];
}

/** Le regole che valgono per l'appalto: le pubbliche se è pubblico, le private altrimenti. */
export function applicableRules(
  rules: readonly RoleRule[],
  isPublicProcurement: boolean | null | undefined,
): RoleRule[] {
  const scope: RoleRuleScope = isPublicProcurement === true ? 'public' : 'private';
  return rules.filter((rule) => rule.appliesTo === 'any' || rule.appliesTo === scope);
}

/**
 * Valuta i ruoli che la persona avrebbe nel cantiere. `otherHolders` dice, per ogni ruolo, quanti
 * altri partecipanti attivi lo hanno già.
 */
export function evaluateRoles(
  roles: readonly string[],
  isPublicProcurement: boolean | null | undefined,
  catalog: readonly ProfessionalRole[],
  rules: readonly RoleRule[],
  otherHolders: Readonly<Record<string, number>> = {},
): RoleEvaluation {
  const distinct = [...new Set(roles)];
  const known = new Map(catalog.map((role) => [role.code, role]));
  const set = new Set(distinct);
  const blocks: RoleIssue[] = [];
  const warnings: RoleIssue[] = [];
  for (const rule of applicableRules(rules, isPublicProcurement)) {
    if (rule.roleA === rule.roleB || !set.has(rule.roleA) || !set.has(rule.roleB)) {
      continue;
    }
    const issue: RoleIssue = {
      rule: rule.code,
      roles: [rule.roleA, rule.roleB],
      message: rule.message,
      reference: rule.reference,
    };
    (rule.kind === 'warn' ? warnings : blocks).push(issue);
  }
  const holders: HolderIssue[] = [];
  for (const code of distinct) {
    const max = known.get(code)?.maxHolders;
    const current = otherHolders[code] ?? 0;
    if (typeof max === 'number' && current >= max) {
      holders.push({ role: code, maxHolders: max, currentHolders: current });
    }
  }
  return {
    blocks,
    warnings,
    holders,
    unknownRoles: distinct.filter((code) => !known.has(code)),
  };
}

/** Gli avvisi non ancora confermati. */
export function unacknowledgedWarnings(
  evaluation: RoleEvaluation,
  acknowledged: readonly string[],
): RoleIssue[] {
  return evaluation.warnings.filter((warning) => !acknowledged.includes(warning.rule));
}

/**
 * Le regole bloccanti che impediscono di aggiungere `candidate` alla selezione attuale: il ruolo
 * si disabilita e la regola ne spiega il motivo.
 */
export function blockingRulesFor(
  candidate: string,
  selected: readonly string[],
  isPublicProcurement: boolean | null | undefined,
  rules: readonly RoleRule[],
): RoleRule[] {
  if (selected.includes(candidate)) {
    return [];
  }
  return applicableRules(rules, isPublicProcurement).filter(
    (rule) =>
      rule.kind === 'block' &&
      rule.roleA !== rule.roleB &&
      ((rule.roleA === candidate && selected.includes(rule.roleB)) ||
        (rule.roleB === candidate && selected.includes(rule.roleA))),
  );
}
